import { FC, useState } from 'react';
import ReactCrop, { Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import styled from 'styled-components';

import { StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import { SidePanelFooter } from '@/components/SidePanel';

const Dialog = styled(StyledDialog)`
  width: 620px;
  padding: 0;
`;

const CropHolder = styled.div`
  padding: 48px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px 4px 0 0;
`;

interface CropModalProps {
  aspectRatio?: number;
  freeFormCrop?: boolean;
  imageSrc: string;
  previousCrop?: Crop;
  onClose: () => void;
  onCrop: (base64: string) => void;
  setPreviousCrop: (crop: Crop | undefined) => void;
}

const CropModal: FC<CropModalProps> = ({
  aspectRatio = 0.95 / 1,
  freeFormCrop = false,
  imageSrc,
  previousCrop,
  onClose,
  onCrop,
  setPreviousCrop,
}) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const [image, setImage] = useState<HTMLImageElement | null>(null);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const img = e.currentTarget;
    const { width, height } = img;

    const initial =
      previousCrop ??
      (freeFormCrop
        ? centerCrop(
            { unit: '%', width: 90, height: 90, x: 5, y: 5 },
            width,
            height,
          )
        : centerCrop(
            makeAspectCrop({ unit: 'px', width }, aspectRatio, width, height),
            width,
            height,
          ));

    setCrop(initial);
    setImage(img);
  }

  const cropImageNow = () => {
    if (!image || !crop?.width || !crop?.height) {
      onClose();
      return;
    }
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = 3;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      onClose();
      return;
    }

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    const sx = crop.x * scaleX;
    const sy = crop.y * scaleY;
    const sw = crop.width * scaleX;
    const sh = crop.height * scaleY;

    ctx.drawImage(
      image,
      sx,
      sy,
      sw,
      sy + sh >= image.naturalHeight ? sh - 4 : sh,
      0,
      0,
      crop.width,
      crop.height,
    );

    const base64Image = canvas.toDataURL('image/jpg');
    onCrop(base64Image);
    setPreviousCrop(crop);
    onClose();
  };

  return (
    <Modal zIndex={1}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <CropHolder>
          {imageSrc && (
            <ReactCrop
              crop={crop}
              aspect={freeFormCrop ? undefined : aspectRatio}
              onChange={(crop) => setCrop(crop)}
              ruleOfThirds
            >
              <img src={imageSrc} alt="crop me" onLoad={onImageLoad} />
            </ReactCrop>
          )}
        </CropHolder>
        <SidePanelFooter $actions>
          <Button type="button" onClick={cropImageNow}>
            Save
          </Button>
          <Button color="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
        </SidePanelFooter>
      </Dialog>
    </Modal>
  );
};

export default CropModal;
